import {
  HttpInterceptorFn,
  HttpErrorResponse,
  HttpStatusCode,
} from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { HandleErrorService } from '../services/handle-error.service';
import { ElxKeycloakService } from '../services/keycloak.service';
import { SessionService } from '../services/session.service';

let isRedirectingToError = false;

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const injector = inject(Injector);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      const handleErrorService = injector.get(HandleErrorService);
      const router = injector.get(Router);
      const authService = injector.get(ElxKeycloakService);
      // Prevent redirect loops
      if (isRedirectingToError) {
        handleErrorService.handleError(error);
        return throwError(() => 'Error during error handling');
      }

      // Skip handling for login/refresh calls themselves to avoid loops
      if (
        req.url.includes('/auth/login') ||
        req.url.includes('/auth/refresh') ||
        req.url.includes('/auth/logout') ||
        req.url.includes('/protocol/openid-connect/token')
      ) {
        return throwError(() => error);
      }

        if (error.status === HttpStatusCode.Forbidden) {
          // Skip global 403 handling for these features to show errors locally (e.g. in formAlert)
          const url = req.url.toLowerCase();
          const isDataProduct = url.includes('data-products') || url.includes('data-product');
          const isDataSource = url.includes('datasources') || url.includes('data-sources');
          const isDataset = url.includes('datasets');
          const isPlatform = url.includes('platforms');
          const isUserManagement = url.includes('users') || url.includes('roles');
          const isOrganization = url.includes('organizations');
          const isSystemSetting = url.includes('system-setting');

          if (isDataProduct || isDataSource || isDataset || isPlatform || isUserManagement || isOrganization || isSystemSetting) {
            return throwError(() => error);
          }

          return handle403Error(router);
        }

      if (error.status === HttpStatusCode.BadRequest) {
        // Some endpoints use 400 for "not configured yet" flows (e.g. S3 connector initial check).
        // Let feature-level components handle these without showing a global unexpected error banner.
        const url = req.url.toLowerCase();
        const isS3ConfigCheck =
          req.method === 'GET' &&
          (url.includes('/s3/config') || url.includes('\\s3\\config')) &&
          (url.includes('data-products') || url.includes('data-product'));

        if (isS3ConfigCheck) {
          return throwError(() => error);
        }

        handleErrorService.handleError(error);
        return throwError(() => error);
      }

      if (error.status === HttpStatusCode.Unauthorized) {
        const sessionService = injector.get(SessionService);

        // Attempt to refresh the token and retry the original request
        return from(authService.forceRefreshToken()).pipe(
          switchMap((newToken) => {
            if (newToken) {
              const modifiedReq = req.clone({
                setHeaders: { Authorization: `Bearer ${newToken}` },
              });
              return next(modifiedReq);
            } else {
              // Refresh failed, notify session service to handle redirection
              sessionService.handleSessionExpired();
              return throwError(() => 'Unauthorized - session expired.');
            }
          }),
          catchError((refreshError) => {
            sessionService.handleSessionExpired();
            return throwError(() => refreshError);
          }),
        );
      }

      handleErrorService.handleError(error);
      return throwError(() => error);
    }),
  );
};

function handle403Error(router: Router) {
  if (!isRedirectingToError) {
    isRedirectingToError = true;
    router.navigate(['/error/403'], { replaceUrl: true }).finally(() => {
      setTimeout(() => {
        isRedirectingToError = false;
      }, 1000);
    });
  }
  return throwError(() => 'Forbidden - redirecting to error page.');
}
